import React from 'react'
import PropTypes from 'prop-types'

/**
 * Post-submission results display. Shown after a student submits
 * (or when revisiting a completed attempt). Respects feedback settings.
 *
 * Props:
 *   results — object from the backend containing:
 *     showPoints   — whether to display point values
 *     taskResults  — array of { taskTitle, taskGuid, simulationGuid?, completed, pointsEarned?, pointsPossible? }
 *     totalEarned  — total points earned (only when showPoints is true)
 *     totalPossible — total possible points (only when showPoints is true)
 *     simulationGroups — optional array of { guid, title } for chain grouping
 */

const TaskRow = ({ tr, index, showPoints }) => (
  <tr key={tr.taskGuid || index} style={{
    borderBottom: '1px solid #e2e6ea',
    background: index % 2 === 0 ? 'transparent' : '#f8faf9',
  }}>
    <td style={{ padding: '6px 8px' }}>{tr.taskTitle || tr.taskGuid}</td>
    <td style={{
      textAlign: 'center',
      padding: '6px 8px',
      color: tr.completed ? '#28a745' : '#dc3545',
      fontWeight: 600,
    }}>
      {tr.completed ? 'Yes' : 'No'}
    </td>
    {showPoints && (
      <td style={{ textAlign: 'right', padding: '6px 8px' }}>
        {tr.pointsEarned != null ? `${tr.pointsEarned} / ${tr.pointsPossible}` : '—'}
      </td>
    )}
  </tr>
)

const TaskTable = ({ tasks, showPoints }) => (
  <table style={{
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  }}>
    <thead>
      <tr style={{ borderBottom: '2px solid #c3e6cb' }}>
        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#495057' }}>Task</th>
        <th style={{ textAlign: 'center', padding: '6px 8px', color: '#495057', width: '80px' }}>Status</th>
        {showPoints && (
          <th style={{ textAlign: 'right', padding: '6px 8px', color: '#495057', width: '100px' }}>Points</th>
        )}
      </tr>
    </thead>
    <tbody>
      {tasks.map((tr, i) => (
        <TaskRow key={tr.taskGuid || i} tr={tr} index={i} showPoints={showPoints} />
      ))}
    </tbody>
  </table>
)

export const ResultsSummary = ({ results }) => {
  if (!results || !results.taskResults) return null

  const { showPoints, taskResults, totalEarned, totalPossible, simulationGroups } = results

  const hasGroups = simulationGroups && simulationGroups.length > 0

  // Build grouped tasks by simulation guid, preserving group order
  let groupedContent = null
  if (hasGroups) {
    const tasksByGuid = new Map()
    for (const group of simulationGroups) {
      tasksByGuid.set(group.guid, [])
    }
    for (const tr of taskResults) {
      const guid = tr.simulationGuid || ''
      if (tasksByGuid.has(guid)) {
        tasksByGuid.get(guid).push(tr)
      } else {
        // Fallback: tasks with unknown guid go into an "ungrouped" bucket
        if (!tasksByGuid.has('')) tasksByGuid.set('', [])
        tasksByGuid.get('').push(tr)
      }
    }
    groupedContent = simulationGroups
      .filter(g => (tasksByGuid.get(g.guid) || []).length > 0)
      .map(group => (
        <div key={group.guid} style={{ marginBottom: '12px' }}>
          <h5 style={{ margin: '12px 0 6px 0', fontSize: '14px', color: '#495057' }}>
            {group.title}
          </h5>
          <TaskTable tasks={tasksByGuid.get(group.guid)} showPoints={showPoints} />
        </div>
      ))
  }

  return (
    <div style={{
      border: '1px solid #c3e6cb',
      borderRadius: '6px',
      background: '#f0faf3',
      padding: '16px',
      marginTop: '12px',
    }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#155724' }}>
        Simulation Complete
      </h4>

      {showPoints && totalEarned != null && (
        <div style={{
          fontSize: '18px',
          fontWeight: 700,
          color: '#155724',
          marginBottom: '12px',
        }}>
          Score: {totalEarned} / {totalPossible}
        </div>
      )}

      {hasGroups ? groupedContent : (
        <TaskTable tasks={taskResults} showPoints={showPoints} />
      )}
    </div>
  )
}

ResultsSummary.propTypes = {
  results: PropTypes.shape({
    showPoints: PropTypes.bool,
    taskResults: PropTypes.arrayOf(PropTypes.shape({
      taskTitle: PropTypes.string,
      taskGuid: PropTypes.string,
      simulationGuid: PropTypes.string,
      completed: PropTypes.bool,
      pointsEarned: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      pointsPossible: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })),
    totalEarned: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalPossible: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    simulationGroups: PropTypes.arrayOf(PropTypes.shape({
      guid: PropTypes.string,
      title: PropTypes.string,
    })),
  }),
}
